// 🧭 ENQUADRAMENTO CLÍNICO — HELPERS DE CLASSIFICAÇÃO DE CENÁRIO
// Sistema: HEPATO TX RONDÔNIA
// Arquivo: src/modules/ia-analise/services/enquadramentoClinico.ts
//
// Objetivo (AI_46): centralizar a lógica que decide COMO a IA deve enquadrar
// a análise de um exame de imagem, substituindo os templates monolíticos que
// presumem cenário oncológico por default.
//
// Três funções exportadas:
//   1. detectarCenarioClinico       — classifica o "propósito" do exame
//   2. detectarEvidenciaHepatopatiaRadiologica — detecta marcadores de HC no laudo
//   3. validarCriterioRiscoCHC      — valida aplicabilidade de LI-RADS
//
// Estas funções são determinísticas e textuais — complementam (não substituem)
// o raciocínio da IA. Elas alimentam o prompt com contexto estruturado.

import { Paciente } from "../../../types";

// ============================================================
// TIPOS EXPORTADOS
// ============================================================

export type CenarioClinico =
  | "estadiamento_pre_tx"
  | "sintoma_agudo"
  | "pos_tx_rotina"
  | "pos_tx_com_sintoma"
  | "indeterminado";

export type CriterioRiscoCHC = "a" | "b" | "c" | "radiologico" | null;

export interface ResultadoValidacaoCHC {
  aplicavel: boolean;
  criterio: CriterioRiscoCHC;
  confianca: "alta" | "media" | "baixa";
  justificativa: string;
}

// Interface mínima para o exame — evita acoplamento à interface triplicada do ExameImagem
// Débito B17 (unificação de interface) segue pendente como sessão separada.
export interface ExameImagemMinimo {
  tipo?: string;
  tipoExame?: string;
  descricao?: string;
  indicacaoClinica?: string;
  laudo?: string;
  resumoAchados?: string;
  achados?: {
    lesoesHepaticas?: string;
    statusMilao?: string;
    conclusao?: string;
    conclusaoLaudistaLiteral?: string;
    outrosAchados?: string;
    shunts?: string;
    esplenomegalia?: string;
  };
}

// ============================================================
// NORMALIZAÇÃO TEXTUAL
// ============================================================

/**
 * Normaliza texto removendo acentos e convertendo para lowercase,
 * facilitando match case-insensitive e acento-insensitive.
 * Padrão NFD já usado em Camada 5 / AI_38.
 */
function normalizar(texto: string | undefined | null): string {
  if (!texto) return "";
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Concatena todas as fontes textuais de uma indicação/contexto, normalizadas.
 * Ordem de prioridade: indicacaoClinica > descricao > resumoAchados.
 */
function extrairTextoRelevante(exame: ExameImagemMinimo): string {
  const partes = [
    exame.indicacaoClinica,
    exame.descricao,
    exame.resumoAchados,
  ].filter(Boolean);
  return normalizar(partes.join(" | "));
}

// ============================================================
// PALAVRAS-CHAVE PARA CLASSIFICAÇÃO
// ============================================================

/**
 * Sintomas que sugerem indicação clínica NÃO-oncológica.
 * Cobrem respiratório (tosse, dispneia, febre), abdominal (dor, distensão),
 * e sistêmicos (perda ponderal, icterícia recente).
 */
const KW_SINTOMA_AGUDO = [
  // respiratórios
  "tosse",
  "dispneia",
  "dispneico",
  "falta de ar",
  "chiado",
  "sibilo",
  "expectora",
  "febre",
  "febril",
  "hipertermia",
  "dor toracica",
  "dor no peito",
  "pleuritica",
  "hemoptise",
  // abdominais
  "dor abdominal",
  "dor em qsd",
  "dor em qse",
  "dor epigastrica",
  "distensao abdominal",
  "nauseas",
  "vomitos",
  "diarreia",
  "icterica",
  "ictericia recente",
  // sistêmicos/mal-estar
  "mal estar",
  "astenia",
  "queda do estado geral",
  "perda ponderal",
  "emagrecimento",
  "hematemese",
  "melena",
  "enterorragia",
  // infecciosos / imunossupressão
  "suspeita de infeccao",
  "infeccao",
  "sepse",
  "pneumonia",
  "bronquite",
];

/**
 * Palavras que indicam indicação claramente oncológica/estadiamento.
 */
const KW_ONCOLOGICO = [
  "estadiamento",
  "rastreamento",
  "rastreio",
  "vigilancia",
  "vigilância",
  "screening",
  "follow up",
  "follow-up",
  "metastase",
  "metastática",
  "avaliacao de recidiva",
  "reavaliacao oncologica",
  "resposta a tace",
  "resposta a ablacao",
  "pos tace",
  "pos ablacao",
  "controle oncologico",
];

// ============================================================
// 1. DETECTAR CENÁRIO CLÍNICO
// ============================================================

/**
 * Classifica o propósito clínico do exame com base em:
 *   (1) status pós/pré-transplante do paciente
 *   (2) presença de CHC ativo no contexto
 *   (3) análise textual da indicação clínica
 *
 * Retorna um dos 5 cenários. "indeterminado" é retornado quando não
 * há sinais claros — a IA recebe instrução conservadora nesse caso.
 */
export function detectarCenarioClinico(
  paciente: Paciente,
  exame: ExameImagemMinimo
): CenarioClinico {
  const texto = extrairTextoRelevante(exame);

  // === 1. Detectar status de Tx ===
  const dataTransplante =
    paciente.inscricaoTx?.dataTransplante || (paciente as any).dataTransplante;
  const isPosTx = Boolean(dataTransplante);

  // === 2. Detectar CHC ativo (pré-Tx, não histórico) ===
  // Se pós-Tx, a SE por CHC é histórica — já resolvida pelo transplante
  const diagBase = normalizar(paciente.inscricaoTx?.diagnosticoBase);
  const temCHCAtivo =
    !isPosTx &&
    (diagBase.includes("carcinoma hepatocelular") ||
      diagBase.includes("chc") ||
      diagBase.includes("hepatocarcinoma") ||
      paciente.inscricaoTx?.situacaoEspecial === true);

  // === 3. Analisar texto da indicação ===
  const temSintomaAgudo = KW_SINTOMA_AGUDO.some((kw) => texto.includes(kw));
  const temIntencaoOncologica = KW_ONCOLOGICO.some((kw) => texto.includes(kw));

  // === 4. Árvore de decisão ===

  // Prioridade absoluta: sintoma agudo sobrescreve qualquer presunção
  // oncológica. Mesmo em paciente com CHC, se o exame foi pedido por
  // tosse, o foco é o sintoma.
  if (temSintomaAgudo && isPosTx) return "pos_tx_com_sintoma";
  if (temSintomaAgudo) return "sintoma_agudo";

  // Cenário oncológico explícito pré-Tx com CHC ativo
  if (temCHCAtivo && temIntencaoOncologica) return "estadiamento_pre_tx";

  // Pré-Tx com CHC sem indicação textual explícita → presume estadiamento
  if (temCHCAtivo) return "estadiamento_pre_tx";

  // Pós-Tx sem sintoma agudo → rotina (infecção oportunista, PTLD,
  // toxicidade medicamentosa ficam no diferencial do template)
  if (isPosTx) return "pos_tx_rotina";

  // Qualquer outro caso: indeterminado. Template deve assumir descrição
  // neutra sem enquadramento oncológico compulsório.
  return "indeterminado";
}

// ============================================================
// 2. EVIDÊNCIA RADIOLÓGICA DE HEPATOPATIA CRÔNICA
// ============================================================

/**
 * Regex pré-compiladas para performance (sem custo de recompilação por chamada).
 * Agrupadas por categoria de evidência.
 */
const REGEX_EVIDENCIA_HC = [
  // Hipertensão portal
  /hipertens[ãa]o\s+portal/i,
  /veia\s+porta\s+(de\s+)?calibre\s+aumentad/i,
  /porta\s+dilatad/i,
  /dilata[çc][ãa]o\s+(da\s+)?veia\s+porta/i,
  // Colaterais
  /recanaliza[çc][ãa]o\s+(da\s+)?(veia\s+)?umbilical/i,
  /varizes?\s+(esofag|gastric|epigastric|periesplen|peripancre)/i,
  /shunts?\s+(portossist|esplenorren|espleno-ren)/i,
  /circula[çc][ãa]o\s+colateral/i,
  /colaterais?\s+(portossist|gastroesof|periesplen)/i,
  // Esplenomegalia
  /esplenomegalia/i,
  /ba[çc]o\s+(aumentad|dimens[õo]es\s+aumentad)/i,
  // Ascite (marcador de HC descompensada)
  /asc[íi]te/i,
  /l[íi]quido\s+livre\s+(abdominal|perito[nn]eal|na\s+cavidade)/i,
  // Morfologia hepática cirrótica
  /contorno\s+(hep[áa]tico\s+)?(irregular|nodular|serril|lobulad)/i,
  /n[óo]dulo\s+regenerativo/i,
  /f[íi]gado\s+nodular/i,
  /redu[çc][ãa]o\s+(do\s+)?(segmento|lobo)\s+(lateral\s+)?(hep[áa]tico\s+)?(esquerd|direit)/i,
  /hipertrofia\s+(do\s+)?caudad/i,
  // Achados sugestivos diretos
  /sinais?\s+de\s+hepatopat/i,
  /sinais?\s+de\s+cirrose/i,
  /aspect\w+\s+cirrot/i,
];

/**
 * Retorna true se o texto contém pelo menos UM marcador radiológico
 * de hepatopatia crônica. Baseado em Baveno VII e AASLD 2024 — critérios
 * que permitem inferência clínica de cirrose/HTP sem biópsia.
 *
 * Uso: valida o critério (a) de aplicabilidade do LI-RADS quando o
 * `diagnosticoBase` do paciente está vazio mas o próprio laudo descreve
 * hepatopatia.
 */
export function detectarEvidenciaHepatopatiaRadiologica(
  texto: string | undefined | null
): boolean {
  if (!texto) return false;
  return REGEX_EVIDENCIA_HC.some((rx) => rx.test(texto));
}

// ============================================================
// 3. VALIDAR CRITÉRIO DE RISCO CHC (LI-RADS APLICABILIDADE)
// ============================================================

/**
 * Valida se o paciente atende a algum critério que torne LI-RADS
 * aplicável. Critérios oficiais (AASLD 2024):
 *
 *   (a) Cirrose documentada de qualquer etiologia
 *   (b) Infecção crônica por HBV (com ou sem cirrose)
 *   (c) CHC atual ou prévio
 *
 * Critério adicional proprietário (AI_46):
 *
 *   (radiologico) — Evidência radiológica de hepatopatia crônica
 *                   no próprio exame ou em exames anteriores.
 *                   Confiança "media", permite ressalva enxuta.
 *
 * Ordem de avaliação: estruturados (a/b/c) primeiro, radiológico apenas
 * se nenhum estruturado estiver satisfeito. Isso garante "alta" confiança
 * sempre que possível.
 */
export function validarCriterioRiscoCHC(
  paciente: Paciente,
  exameAtual: ExameImagemMinimo,
  examesAnteriores: ExameImagemMinimo[] = []
): ResultadoValidacaoCHC {
  const diagBase = normalizar(paciente.inscricaoTx?.diagnosticoBase);

  // === Critério (a): Cirrose documentada ===
  const temCirroseDocumentada =
    diagBase.includes("cirrose") ||
    diagBase.includes("hepatopatia cronica") ||
    diagBase.includes("hepatopatia crônica");

  if (temCirroseDocumentada) {
    return {
      aplicavel: true,
      criterio: "a",
      confianca: "alta",
      justificativa: "Cirrose documentada no diagnóstico de base do paciente.",
    };
  }

  // === Critério (b): HBV crônico ===
  const temHBV =
    diagBase.includes("hepatite b") ||
    diagBase.includes("hbv") ||
    diagBase.includes("vhb");

  if (temHBV) {
    return {
      aplicavel: true,
      criterio: "b",
      confianca: "alta",
      justificativa:
        "Infecção crônica por HBV documentada — LI-RADS aplicável mesmo sem cirrose.",
    };
  }

  // === Critério (c): CHC atual ou prévio ===
  const temCHCDiagBase =
    diagBase.includes("carcinoma hepatocelular") ||
    diagBase.includes("chc") ||
    diagBase.includes("hepatocarcinoma");

  const temCHCPrevioEmExames = examesAnteriores.some((e) => {
    const lesoes = normalizar(e.achados?.lesoesHepaticas);
    const conclusao = normalizar(e.achados?.conclusao);
    const literal = normalizar(e.achados?.conclusaoLaudistaLiteral);
    return (
      lesoes.includes("li-rads 5") ||
      lesoes.includes("lr-5") ||
      lesoes.includes("lr5") ||
      conclusao.includes("chc") ||
      literal.includes("carcinoma hepatocelular") ||
      literal.includes("hepatocarcinoma")
    );
  });

  if (temCHCDiagBase || temCHCPrevioEmExames) {
    return {
      aplicavel: true,
      criterio: "c",
      confianca: "alta",
      justificativa: temCHCDiagBase
        ? "CHC documentado no diagnóstico de base."
        : "CHC prévio identificado em exames anteriores do paciente.",
    };
  }

  // === Critério radiológico (AI_46, proprietário) ===
  // Se nenhum estruturado satisfeito, buscar evidência no próprio exame
  // ou em anteriores.
  const textosParaAnalise: string[] = [
    exameAtual.indicacaoClinica,
    exameAtual.descricao,
    exameAtual.resumoAchados,
    exameAtual.achados?.lesoesHepaticas,
    exameAtual.achados?.conclusao,
    exameAtual.achados?.conclusaoLaudistaLiteral,
    exameAtual.achados?.outrosAchados,
    exameAtual.achados?.shunts,
    exameAtual.achados?.esplenomegalia,
    exameAtual.laudo,
  ].filter((t): t is string => Boolean(t));

  for (const e of examesAnteriores) {
    if (e.resumoAchados) textosParaAnalise.push(e.resumoAchados);
    if (e.achados?.lesoesHepaticas)
      textosParaAnalise.push(e.achados.lesoesHepaticas);
    if (e.achados?.conclusao) textosParaAnalise.push(e.achados.conclusao);
    if (e.achados?.conclusaoLaudistaLiteral)
      textosParaAnalise.push(e.achados.conclusaoLaudistaLiteral);
    if (e.achados?.outrosAchados)
      textosParaAnalise.push(e.achados.outrosAchados);
    if (e.achados?.shunts) textosParaAnalise.push(e.achados.shunts);
    if (e.achados?.esplenomegalia)
      textosParaAnalise.push(e.achados.esplenomegalia);
  }

  const textoUnificado = textosParaAnalise.join(" | ");
  const temEvidenciaRadiologica =
    detectarEvidenciaHepatopatiaRadiologica(textoUnificado);

  if (temEvidenciaRadiologica) {
    return {
      aplicavel: true,
      criterio: "radiologico",
      confianca: "media",
      justificativa:
        "Sinais radiológicos de hepatopatia crônica presentes no laudo (HTP, colaterais, esplenomegalia, morfologia cirrótica) — LI-RADS aplicável por inferência imagiológica. Confirmação clínica da etiologia recomendada mas não bloqueante para classificação.",
    };
  }

  // === Nenhum critério satisfeito ===
  return {
    aplicavel: false,
    criterio: null,
    confianca: "baixa",
    justificativa:
      "Nenhum critério de risco para CHC documentado: ausência de cirrose, HBV crônico, CHC prévio ou evidência radiológica de hepatopatia crônica. LI-RADS tecnicamente não validado para este contexto — ressalva metodológica apropriada se aplicado.",
  };
}

// ============================================================
// 4. GERAR BLOCO DE INDICAÇÃO CLÍNICA PARA PROMPT
// ============================================================

/**
 * Gera o bloco de texto que será injetado no prompt ANTES do template
 * específico, ancorando o raciocínio da IA no propósito real do exame.
 *
 * Este bloco é o "coração" do fix da 9ª falha: converte o campo estruturado
 * `indicacaoClinica` em instrução explícita para o modelo, e na ausência do
 * campo, instrui política conservadora em vez da presunção oncológica atual.
 *
 * Uso: chamar em gerarPromptExameImagem.ts, entre o cabeçalho de dados do
 * paciente e o template específico retornado por getTemplateEspecifico.
 */
export function gerarBlocoIndicacaoClinica(
  exame: ExameImagemMinimo,
  cenario: CenarioClinico
): string {
  const indicacao = (exame.indicacaoClinica || "").trim();
  const temIndicacao = indicacao.length > 0;

  // Cabeçalho do bloco — sempre presente, tom de instrução direta ao modelo.
  let bloco = `\n\n## 🎯 CONTEXTO CLÍNICO DO EXAME (ÂNCORA DE ENQUADRAMENTO)\n\n`;

  // Linha 1 — indicação clínica (ou ausência dela).
  if (temIndicacao) {
    bloco += `**Indicação clínica declarada pelo solicitante:** "${indicacao}"\n\n`;
  } else {
    bloco += `**Indicação clínica:** NÃO DOCUMENTADA no prontuário.\n\n`;
  }

  // Linha 2 — cenário clínico detectado.
  bloco += `**Cenário clínico detectado (análise determinística):** \`${cenario}\`\n\n`;

  // === Instruções por cenário ===
  bloco += `### 📌 Instruções de enquadramento para este exame:\n\n`;

  switch (cenario) {
    case "estadiamento_pre_tx":
      bloco += `- Paciente pré-transplante com CHC ativo ou indicação oncológica explícita.\n`;
      bloco += `- **Enquadramento oncológico é APROPRIADO** — aplicar critérios de estadiamento, LI-RADS, Milão conforme relevância topográfica.\n`;
      bloco += `- Busca ativa por metástases, progressão tumoral, invasão vascular é LEGÍTIMA neste cenário.\n`;
      break;

    case "sintoma_agudo":
      bloco += `- Exame foi solicitado para investigar SINTOMA AGUDO — NÃO é estadiamento nem rastreamento oncológico.\n`;
      bloco += `- **O foco analítico primário DEVE ser a indicação clínica declarada** — responder à pergunta do médico solicitante.\n`;
      bloco += `- Achados incidentais devem ser reportados de forma breve, mas SEM presumir cenário oncológico se não houver gatilho específico no laudo.\n`;
      bloco += `- EVITE classificar nódulos inflamatórios/sequelares como "suspeitos de malignidade" por default.\n`;
      break;

    case "pos_tx_com_sintoma":
      bloco += `- Paciente pós-transplante hepático apresentando sintoma agudo.\n`;
      bloco += `- **Diferenciais prioritários neste contexto** (imunossupressão crônica):\n`;
      bloco += `  1. Infecção oportunista (viral, bacteriana, fúngica — incluindo Aspergillus, CMV, PCP)\n`;
      bloco += `  2. PTLD (Post-Transplant Lymphoproliferative Disorder)\n`;
      bloco += `  3. Toxicidade medicamentosa (pneumonite por mTOR inhibitors — sirolimus/everolimus)\n`;
      bloco += `  4. Complicações do enxerto (se exame topograficamente compatível)\n`;
      bloco += `  5. Doenças crônicas associadas à imunossupressão prolongada\n`;
      bloco += `- **A presunção de recidiva oncológica só é legítima se houver gatilho radiológico específico** (nódulo hipervascular novo, padrão sugestivo). Sintoma clínico isolado NÃO é gatilho.\n`;
      bloco += `- EVITE classificar achados inflamatórios/infecciosos como "suspeitos de metástase".\n`;
      break;

    case "pos_tx_rotina":
      bloco += `- Paciente pós-transplante em seguimento de rotina sem sintoma agudo documentado.\n`;
      bloco += `- **Enquadramento descritivo neutro** — reportar achados sem hipótese diagnóstica dirigida.\n`;
      bloco += `- Diferenciais a considerar quando há achados relevantes: complicações do enxerto, imunossupressão crônica, recidiva da doença de base.\n`;
      bloco += `- NÃO presumir cenário oncológico agudo sem gatilho radiológico específico.\n`;
      break;

    case "indeterminado":
    default:
      bloco += `- Cenário clínico não classificável com os dados disponíveis.\n`;
      bloco += `- **Adote política conservadora:** descrição neutra dos achados, sem enquadramento oncológico compulsório, sem hipóteses diagnósticas dirigidas.\n`;
      bloco += `- Se a indicação clínica não foi documentada, mencione brevemente esta limitação e sugira correlação clínica.\n`;
      bloco += `- EVITE presumir cenário de estadiamento, rastreio ou vigilância sem gatilho explícito.\n`;
      break;
  }

  bloco += `\n---\n`;
  return bloco;
}
